import { useContext, useEffect, useState } from 'react';
import { TextButton, Input } from 't-lib-ui/widgets';
import { closeIcon } from '../icons';
import { Link } from 't-lib-ui/router';
import { nonNull } from 't-lib-ui/helpers';
import { AppState } from '../../context/general';
import { getAutocomplete, getFeedSummaries } from '../../connector/api';
import { RestCatalogAutocomplete, RestFeedSummary } from '../../schema-copy/api-schema';

interface FeedAutocompletePickerProps {
  /** Current selected feed IDs */
  value: string[];
  /** Called when the selection changes */
  onChange: (next: string[]) => void;
  /** Optional ID to exclude from suggestions (typically the catalog being edited) */
  excludeId?: string;
  /** Placeholder for the search input */
  placeholder?: string;
  /** When set, each selected feed's name links to the returned (same-origin) path */
  itemHref?: (feedId: string) => string;
}

const FeedAutocompletePicker: React.FC<FeedAutocompletePickerProps> = ({ value, onChange, excludeId, placeholder, itemHref }) => {
  const appState = useContext(AppState);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<RestCatalogAutocomplete>([]);
  const [summaries, setSummaries] = useState<Record<string, RestFeedSummary>>({});

  // Debounced autocomplete lookup
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const results = await getAutocomplete(nonNull(appState.state.authData), query.trim());
        setSuggestions(results.filter(([id]) => id != excludeId && !value.includes(id)));
      } catch (e) {
        console.error('Autocomplete failed:', e);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, value, excludeId, appState.state.authData]);

  // Resolve summaries for the current selection (icon + name display)
  useEffect(() => {
    if (!value.length || !appState.state.authData) {
      setSummaries({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await getFeedSummaries(nonNull(appState.state.authData), value);
        if (cancelled) return;
        const map: Record<string, RestFeedSummary> = {};
        for (const s of results) map[s.id] = s;
        setSummaries(map);
      } catch (e) {
        if (!cancelled) console.error('Failed to load feed summaries:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [value.join(','), appState.state.authData]);

  const handleSelect = (feedId: string) => {
    if (value.includes(feedId)) return;
    onChange([...value, feedId]);
    setQuery('');
    setSuggestions([]);
  };

  const handleRemove = (feedId: string) => {
    onChange(value.filter(id => id != feedId));
  };

  return (
    <div className="feedAutocompletePicker" style={{ position: 'relative' }}>
      <Input
        value={query}
        onInput={setQuery}
        placeholder={placeholder ?? 'Search feeds by name…'}
        layout="singleCol"
      />
      {suggestions.length > 0 && (
        <ul style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          zIndex: 10,
          background: 'var(--surface-raised)',
          border: '1px solid var(--gray-850)',
          borderRadius: '4px',
          margin: 0,
          padding: 0,
          listStyle: 'none',
          maxHeight: '240px',
          overflowY: 'auto',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          {suggestions.slice(0, 12).map(([id, title, domain]) => (
            <li key={id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(id); }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}
              >
                <strong>{title}</strong>
                <span style={{ color: 'var(--gray-500)', marginLeft: '8px', fontSize: '0.85rem' }}>{domain}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {value.length > 0 && (
        <ul style={{
          listStyle: 'none',
          margin: '12px 0 0 0',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          {value.map(id => {
            const s = summaries[id];
            return (
              <li key={id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                border: '1px solid var(--gray-850)',
                borderRadius: '6px',
                background: 'var(--gray-975)',
                fontSize: '0.9rem'
              }}>
                {s?.iconUrl
                  ? <img src={s.iconUrl} alt="" width={16} height={16} style={{ flex: '0 0 16px' }} />
                  : <span style={{ flex: '0 0 16px' }}>📰</span>}
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {itemHref ? <Link to={itemHref(id)} focussable="sideways">{s?.shortName ?? id}</Link> : s?.shortName ?? id}
                  {s?.domain && <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginLeft: '8px' }}>{s.domain}</span>}
                </span>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.75rem' }}>{id}</span>
                <TextButton
                  icon={closeIcon}
                  onClick={() => handleRemove(id)}
                  type="minor"
                  size="small"
                  focussable="sideway"
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FeedAutocompletePicker;
